import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

// ═══════════════ GET FARE CONFIG ═══════════════
// NSW CTP (Green Slip) per-km charge.
// Region-aware metro vs country rate, judged by pickup coords. Mirrors the
// server helper in functions/bookings/bookingFunctions.js. Defaults to metro
// when coords are missing. Config comes from config/fareRates (rates.ctp).
const DEFAULT_CTP_CONFIG = {
  metroRatePerKm: 0.10,
  countryRatePerKm: 0.066,
  metroBoxes: [
    { latMin: -34.20, latMax: -33.40, lngMin: 150.50, lngMax: 151.45 }, // Greater Sydney
    { latMin: -33.55, latMax: -33.18, lngMin: 151.10, lngMax: 151.65 }, // Central Coast
    { latMin: -33.10, latMax: -32.70, lngMin: 151.45, lngMax: 152.10 }, // Newcastle / Hunter
    { latMin: -34.75, latMax: -34.25, lngMin: 150.65, lngMax: 151.05 }, // Wollongong / Illawarra
  ],
};
function resolveCtpConfig(cfg) {
  const c = cfg || {};
  return {
    metroRatePerKm: typeof c.metroRatePerKm === 'number' ? c.metroRatePerKm : DEFAULT_CTP_CONFIG.metroRatePerKm,
    countryRatePerKm: typeof c.countryRatePerKm === 'number' ? c.countryRatePerKm : DEFAULT_CTP_CONFIG.countryRatePerKm,
    metroBoxes: Array.isArray(c.metroBoxes) && c.metroBoxes.length ? c.metroBoxes : DEFAULT_CTP_CONFIG.metroBoxes,
  };
}
function isCountryNSW(lat, lng, metroBoxes) {
  const boxes = Array.isArray(metroBoxes) && metroBoxes.length ? metroBoxes : DEFAULT_CTP_CONFIG.metroBoxes;
  if (typeof lat !== 'number' || typeof lng !== 'number') return false; // default metro
  return !boxes.some(
    (b) => lat >= b.latMin && lat <= b.latMax && lng >= b.lngMin && lng <= b.lngMax
  );
}
export function computeCtp(distanceKm, lat, lng, ctpConfig) {
  const cfg = resolveCtpConfig(ctpConfig);
  const country = isCountryNSW(lat, lng, cfg.metroBoxes);
  const rate = country ? cfg.countryRatePerKm : cfg.metroRatePerKm;
  return Math.round((distanceKm || 0) * rate * 100) / 100;
}

export async function getFareConfig() {
  const ratesDoc = await getDoc(doc(db, 'config', 'fareRates'));
  const peakDoc = await getDoc(doc(db, 'config', 'peakPricing'));
  return {
    rates: ratesDoc.exists() ? ratesDoc.data() : getDefaultRates(),
    peak: peakDoc.exists() ? peakDoc.data() : { windows: [], dateOverrides: [] },
  };
}

function getDefaultRates() {
  return {
    tiers: {
      go: { baseFare: 2.0, perKm: 1.5, perMin: 0.40 },
      comfy: { baseFare: 4.0, perKm: 1.8, perMin: 0.50 },
      plus: { baseFare: 6.0, perKm: 2.8, perMin: 0.70 },
      vip: { baseFare: 10.0, perKm: 3.5, perMin: 0.90 },
      vipPlus: { baseFare: 15.0, perKm: 5.0, perMin: 1.25 },
    },
    levy: 1.2,
    gstRate: 0.1,
    minFare: 18.0,
    waitingFreeMinutes: 5,
    waitingFeePerMin: 1.1,
    // Multi-stop dwell (Batch 11) — applies to each intermediate stop
    stopDwellFreeMinutes: 1,
    stopDwellRatePerMin: 0.5,
  };
}

// ═══════════════ GET SYSTEM CONFIG ═══════════════
// Trip booking-window settings (admin-editable via config/system).
export async function getSystemConfig() {
  const snap = await getDoc(doc(db, 'config', 'system'));
  const d = snap.exists() ? snap.data() : {};
  return {
    ...d,
    minAdvanceBookingMinutes: typeof d.minAdvanceBookingMinutes === 'number' ? d.minAdvanceBookingMinutes : 60,
    maxAdvanceBookingDays: typeof d.maxAdvanceBookingDays === 'number' ? d.maxAdvanceBookingDays : 30,
  };
}

// ═══════════════ CHECK PEAK PRICING ═══════════════
export function getPeakSurcharge(pickupTime, peakConfig) {
  const dateStr = pickupTime.toISOString().split('T')[0];
  const override = peakConfig.dateOverrides?.find((o) => o.date === dateStr);
  if (override) return override.surchargePercent / 100;

  const dayOfWeek = pickupTime.getDay(); // 0=Sun
  const timeStr =
    String(pickupTime.getHours()).padStart(2, '0') +
    ':' +
    String(pickupTime.getMinutes()).padStart(2, '0');

  for (const w of peakConfig.windows || []) {
    if (w.dayOfWeek.includes(dayOfWeek) && timeStr >= w.startTime && timeStr <= w.endTime) {
      return w.surchargePercent / 100;
    }
  }
  return 0;
}

// ═══════════════ ESTIMATE FARE (client-side) ═══════════════
// stops parameter is optional. Each stop adds dwell charges per the configured free buffer + per-minute rate.
export function estimateFare({ distanceKm, durationMin, tier, peakMultiplier = 0, tolls = 0, ctp = 0, rates, stops = [] }) {
  const tierRates = rates.tiers[tier];
  if (!tierRates) throw new Error(`Unknown tier: ${tier}`);

  const baseFare = tierRates.baseFare;
  const distanceFare = distanceKm * tierRates.perKm;
  const durationFare = durationMin * tierRates.perMin;

  // Multi-stop dwell (Batch 11)
  const dwellFreeMin = typeof rates.stopDwellFreeMinutes === 'number' ? rates.stopDwellFreeMinutes : 1;
  const dwellRate = typeof rates.stopDwellRatePerMin === 'number' ? rates.stopDwellRatePerMin : 0.5;
  let dwellCharges = 0;
  if (Array.isArray(stops) && stops.length > 0) {
    for (const s of stops) {
      const dwellSec = typeof s.actualDwellSeconds === 'number'
        ? s.actualDwellSeconds
        : (typeof s.expectedDwellSeconds === 'number' ? s.expectedDwellSeconds : 60);
      const dwellMin = dwellSec / 60;
      const chargeable = Math.max(0, dwellMin - dwellFreeMin);
      dwellCharges += chargeable * dwellRate;
    }
    dwellCharges = round(dwellCharges);
  }

  const subtotal = baseFare + distanceFare + durationFare;
  const peakSurcharge = subtotal * peakMultiplier;
  const levy = rates.levy;
  const perStopFee = typeof rates.perStopFee === 'number' ? rates.perStopFee : 5;
  const stopFeeTotal = Array.isArray(stops) ? round(stops.length * perStopFee) : 0;
  const subtotalWithExtras = subtotal + peakSurcharge + tolls + dwellCharges + stopFeeTotal + levy + ctp;
  const gst = subtotalWithExtras * rates.gstRate;
  // Booking fee: flat, GST-inclusive, added after GST and the min-fare floor (mirrors backend calculateFare).
  const bookingFee = typeof rates.bookingFee === 'number' ? rates.bookingFee : 1.0;
  const totalFare = Math.max(round(subtotalWithExtras + gst), rates.minFare) + round(bookingFee);

  return {
    baseFare: round(baseFare),
    distanceFare: round(distanceFare),
    durationFare: round(durationFare),
    peakSurcharge: round(peakSurcharge),
    peakPercent: Math.round(peakMultiplier * 100),
    tolls: round(tolls),
    dwellCharges: round(dwellCharges),
    stopFeeTotal: round(stopFeeTotal),
    stopsCount: Array.isArray(stops) ? stops.length : 0,
    levy: round(levy),
    ctp: round(ctp),
    bookingFee: round(bookingFee),
    gst: round(gst),
    totalFare: round(totalFare),
    tier,
    distanceKm: round(distanceKm),
    durationMin: Math.round(durationMin),
  };
}

// ═══════════════ ESTIMATE ALL TIERS ═══════════════
export function estimateAllTiers({ distanceKm, durationMin, peakMultiplier = 0, tolls = 0, ctp = 0, rates, stops = [] }) {
  const availableTiers = Object.keys(rates.tiers);
  return availableTiers.map((tier) =>
    estimateFare({ distanceKm, durationMin, tier, peakMultiplier, tolls, ctp, rates, stops })
  );
}

// ═══════════════ SERVER-SIDE FARE CALCULATION ═══════════════
export async function calculateFareServer(bookingData) {
  const calc = httpsCallable(functions, 'calculateFare');
  const result = await calc(bookingData);
  return result.data;
}

function round(n) {
  return Math.round(n * 100) / 100;
}